import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { LineChart } from '@mui/x-charts/LineChart';

import { marginReportService } from '../../services/marginReportService';
import type { MarginBucket, MarginTotals, ReportGroupBy } from '../../types/report';
import { ReportDateRangeControls } from './ReportDateRangeControls';
import { useReportDateRange } from './useReportDateRange';

export default function MarginReportPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const companyId = Number(id);

  const {
    data: { preset, from, to },
    actions: { setFrom, setTo, handlePresetChange },
  } = useReportDateRange();
  const [groupBy, setGroupBy] = useState<ReportGroupBy>('day');

  const [marginBuckets, setMarginBuckets] = useState<MarginBucket[]>([]);
  const [marginTotals, setMarginTotals] = useState<MarginTotals | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!companyId) return;
    let isCancelled = false;

    setLoading(true);
    setError(null);

    marginReportService.getReport(companyId, from, to, groupBy)
      .then(response => {
        if (!isCancelled) {
          setMarginBuckets(response.buckets);
          setMarginTotals(response.totals);
        }
      })
      .catch(err => {
        console.error('Failed to load margin report:', err);
        if (!isCancelled) setError('Не вдалося завантажити звіт по маржі');
      })
      .finally(() => {
        if (!isCancelled) setLoading(false);
      });

    return () => { isCancelled = true; };
  }, [companyId, from, to, groupBy]);

  const marginLabels = useMemo(() => marginBuckets.map(b => b.period), [marginBuckets]);
  const marginSeries = useMemo(() => marginBuckets.map(b => b.margin), [marginBuckets]);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          backgroundColor: 'background.default',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1,
          mb: 2,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/companies/${companyId}/reports`)}
          size="small"
        >
          Назад
        </Button>
        <Typography
          variant="button"
          component="h1"
          noWrap
          sx={{
            minWidth: 0,
            pr: { xs: '9px', sm: '10px' },
            color: 'primary.main',
          }}
        >
          Маржа
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Виручка мінус собівартість саме проданого товару. На відміну від руху коштів, велика
        закупівля не «з'їдає» місяць — вона впливає на маржу лише тоді, коли товар продано.
      </Alert>

      <ReportDateRangeControls
        preset={preset}
        onPresetChange={handlePresetChange}
        from={from}
        to={to}
        onFromChange={setFrom}
        onToChange={setTo}
        groupBy={groupBy}
        onGroupByChange={setGroupBy}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {marginTotals && !loading && (
        <Paper sx={{ p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">Виручка</Typography>
            <Typography variant="h5">${marginTotals.revenue.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">Собівартість</Typography>
            <Typography variant="h5">${marginTotals.cost.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">Маржа за період</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: marginTotals.margin >= 0 ? 'success.main' : 'error.main' }}>
              ${marginTotals.margin.toFixed(2)}
            </Typography>
          </Box>
        </Paper>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : marginBuckets.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Немає даних за обраний період</Typography>
        </Paper>
      ) : (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <LineChart
              height={300}
              xAxis={[{ data: marginLabels, scaleType: 'band' }]}
              series={[{ data: marginSeries, label: 'Маржа', color: '#2e7d32' }]}
            />
          </Paper>

          <Paper sx={{ overflow: 'auto', maxHeight: 400 }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <Box component="thead" sx={{ position: 'sticky', top: 0, backgroundColor: 'background.paper', zIndex: 1 }}>
                <Box component="tr">
                  {['Період', 'Виручка', 'Собівартість', 'Маржа'].map(header => (
                    <Box
                      component="th"
                      key={header}
                      sx={{ textAlign: 'right', p: 1, borderBottom: '1px solid', borderColor: 'divider', '&:first-of-type': { textAlign: 'left' } }}
                    >
                      {header}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {marginBuckets.map(bucket => (
                  <Box component="tr" key={bucket.period}>
                    <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>{bucket.period}</Box>
                    <Box component="td" sx={{ p: 1, textAlign: 'right', borderBottom: '1px solid', borderColor: 'divider' }}>${bucket.revenue.toFixed(2)}</Box>
                    <Box component="td" sx={{ p: 1, textAlign: 'right', borderBottom: '1px solid', borderColor: 'divider' }}>${bucket.cost.toFixed(2)}</Box>
                    <Box component="td" sx={{ p: 1, textAlign: 'right', borderBottom: '1px solid', borderColor: 'divider', fontWeight: 600, color: bucket.margin >= 0 ? 'success.main' : 'error.main' }}>
                      ${bucket.margin.toFixed(2)}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
}
